import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
/* eslint-disable no-unused-vars */
import { motion, AnimatePresence } from 'framer-motion';
import { DollarSign, Loader2 } from 'lucide-react';
import toast from '../../../utils/toast';
import '../../../styles/modern-design-system.css';

// Cross-browser compatibility polyfills
const safeTrim = (str) => {
  if (typeof str !== 'string') return '';
  return str.trim ? str.trim() : str.replace(/^\s+|\s+$/g, '');
};

const CommissionEditModal = ({ 
  isOpen, 
  onClose, 
  trip, 
  onCommissionUpdate 
}) => {
  const [commissionAmount, setCommissionAmount] = useState('');
  const [commissionRate, setCommissionRate] = useState('');
  const [tripValue, setTripValue] = useState('');
  const [saving, setSaving] = useState(false);
  const tripValueInputRef = useRef(null);
  const commissionAmountInputRef = useRef(null);

  // Currency formatting function — guard so it never throws and never breaks typing
  const formatCurrency = (value) => {
    const raw = String(value ?? '');
    const numericValue = raw.replace(/[^\d.]/g, '');
    
    // Handle multiple decimal points - keep only the first one
    const parts = numericValue.split('.');
    if (parts.length > 2) {
      const integerPart = parts[0];
      const decimalPart = parts.slice(1).join('');
      return `${integerPart}.${decimalPart}`;
    }
    
    // If there's a decimal point, limit to 2 decimal places
    if (parts.length === 2) {
      const integerPart = parts[0];
      const decimalPart = parts[1].slice(0, 2);
      return `${integerPart}.${decimalPart}`;
    }
    
    return numericValue;
  };

  // Initialize form only when modal opens or trip id changes — not on every trip reference change (so typing isn’t reset)
  useEffect(() => {
    if (isOpen && trip) {
      const commission = trip.commission || {};
      const amt = commission.amount;
      const rate = commission.rate;
      const val = commission.tripValue;
      setCommissionAmount(amt !== undefined && amt !== null ? String(amt) : '');
      setCommissionRate(rate !== undefined && rate !== null ? String(rate) : '');
      setTripValue(val !== undefined && val !== null ? String(val) : '');
    }
  }, [isOpen, trip?.tripid]);

  // Sync state to portaled inputs so typing updates the DOM when React doesn't re-render the portal tree
  useEffect(() => {
    if (tripValueInputRef.current != null) tripValueInputRef.current.value = tripValue ?? '';
    if (commissionAmountInputRef.current != null) commissionAmountInputRef.current.value = commissionAmount ?? '';
  }, [tripValue, commissionAmount]); // eslint-disable-line react-hooks/exhaustive-deps -- omit `trip` so parent re-renders don’t reset form

  const handleSave = async () => {
    if (!commissionAmount && !commissionRate) {
      toast.error('Please enter either commission amount or rate');
      return;
    }

    setSaving(true);
    try {
      const commissionData = {
        tripId: trip.tripid,
        amount: commissionAmount ? parseFloat(commissionAmount) : null,
        rate: commissionRate ? parseFloat(commissionRate) / 100 : null,
        tripValue: tripValue ? parseFloat(tripValue) : null,
        status: 'pending'
      };

      // Save to database via API
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/trips/${trip.tripid}/commission`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(commissionData),
      });

      if (!response.ok) {
        throw new Error('Failed to save commission data');
      }

      // Call parent callback to update local state
      if (onCommissionUpdate) {
        await onCommissionUpdate(commissionData);
      }

      // Dispatch event to update the Commissions page
      window.dispatchEvent(new CustomEvent('commissionUpdated', {
        detail: { 
          tripId: trip.tripid,
          commissionData: commissionData,
          action: 'updated'
        }
      }));

      toast.success('Commission updated successfully!');
      onClose();
    } catch (error) {
      console.error('Error updating commission:', error);
      toast.error('Failed to update commission');
    } finally {
      setSaving(false);
    }
  };

  const calculateAmount = (rate, value) => {
    const actualRate = rate || commissionRate;
    const actualValue = value || tripValue;
    if (actualRate && actualValue) {
      const amount = (parseFloat(actualValue) * parseFloat(actualRate) / 100);
      return amount.toFixed(2);
    }
    return '';
  };

  const calculateRate = (amount, value) => {
    const actualAmount = amount || commissionAmount;
    const actualValue = value || tripValue;
    if (actualAmount && actualValue && parseFloat(actualValue) > 0) {
      const rate = ((parseFloat(actualAmount) / parseFloat(actualValue)) * 100);
      return rate.toFixed(2);
    }
    return '';
  };

  if (!isOpen) return null;

  return createPortal(
    <AnimatePresence>
      <div
        className="flex fixed inset-0 z-50 justify-center items-center px-4 bg-black/40 sm:px-6"
        onClick={onClose}
        role="presentation"
      >
        {/* Modal: stop propagation so clicking inside doesn't close */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="p-5 w-full max-w-2xl bg-white rounded-2xl border border-gray-200 shadow-2xl animate-fade-in-up max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
        >
          {/* Header */}
          <div className="flex justify-between items-center mb-3">
            <div>
              <h2 className="mb-1 text-xl font-bold text-gray-900">
                Edit Commission
              </h2>
              <p className="text-xs text-gray-500">
                Update commission details for this trip
              </p>
            </div>
          </div>

          {/* Content */}
          <div className="space-y-2.5">
            <div>
              <label className="block mb-1 text-sm font-semibold leading-tight text-gray-800" style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}>
                Trip Value ($) <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 w-4 h-4 text-gray-400 -translate-y-1/2 pointer-events-none" aria-hidden />
                <input
                  ref={tripValueInputRef}
                  type="text"
                  defaultValue={tripValue ?? ''}
                  onChange={(e) => {
                    const formatted = formatCurrency(e.target.value);
                    setTripValue(formatted);
                  }}
                  placeholder="0.00"
                  maxLength={12}
                  className="px-3 py-2 pl-9 w-full font-medium text-white bg-gray-700 rounded-lg border border-gray-600 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-gray-500 placeholder:text-gray-300"
                  style={{ 
                    height: '36px',
                    fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif',
                    fontSize: '14px'
                  }}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label className="block mb-1 text-sm font-semibold leading-tight text-gray-800" style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}>
                  Commission Amount ($)
                </label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 w-4 h-4 text-gray-400 -translate-y-1/2 pointer-events-none" aria-hidden />
                  <input
                    ref={commissionAmountInputRef}
                    type="text"
                    defaultValue={commissionAmount ?? ''}
                    onChange={(e) => {
                      const formatted = formatCurrency(e.target.value);
                      setCommissionAmount(formatted);
                      if (tripValue && formatted && parseFloat(tripValue) > 0) {
                        const newRate = calculateRate(formatted, tripValue);
                        setCommissionRate(newRate);
                      }
                    }}
                    placeholder="0.00"
                    maxLength={12}
                    className="px-3 py-2 pl-9 w-full font-medium text-white bg-gray-700 rounded-lg border border-gray-600 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-gray-500 placeholder:text-gray-300"
                    style={{ 
                      height: '36px',
                      fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif',
                      fontSize: '14px'
                    }}
                  />
                </div>
              </div>

              <div>
                <label className="block mb-1 text-sm font-semibold leading-tight text-gray-800" style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}>
                  Commission Rate (%)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={commissionRate}
                  onChange={(e) => {
                    let newRate = e.target.value;
                    // When field shows "0.0000" (or similar), first keypress should replace it, not append
                    if (/^0\.0*$/.test(String(commissionRate).trim()) && /^0\.0*/.test(newRate)) {
                      newRate = newRate.replace(/^0\.0*/, '') || newRate;
                    }
                    setCommissionRate(newRate);
                    if (tripValue && newRate && parseFloat(tripValue) > 0) {
                      const newAmount = calculateAmount(newRate, tripValue);
                      setCommissionAmount(newAmount);
                    }
                  }}
                  placeholder="0.00"
                  className="px-3 py-2 w-full font-medium text-white bg-gray-700 rounded-lg border border-gray-600 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-gray-500 placeholder:text-gray-300"
                  style={{ 
                    height: '36px',
                    fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif',
                    fontSize: '14px'
                  }}
                />
              </div>
            </div>

            {(commissionAmount || commissionRate) && tripValue && (
              <div className="p-3 bg-green-50 rounded-md border border-green-200">
                <p className="text-sm text-green-800">
                  <strong>Calculated:</strong> ${commissionAmount} 
                  ({commissionRate}%)
                </p>
                <p className="mt-1 text-xs text-green-600">
                  Trip Value: ${tripValue} × {commissionRate}% = ${commissionAmount}
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex gap-3 pt-2">
            <button
              onClick={onClose}
              disabled={saving}
              className="flex-1 btn btn-secondary"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || (!commissionAmount && !commissionRate)}
              className="flex-1 btn btn-magic"
            >
              {saving ? (
                <div className="flex justify-center items-center">
                  <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                  Saving...
                </div>
              ) : (
                "Save Commission"
              )}
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>,
    document.body
  );
};

export default CommissionEditModal;
